import type { ComponentType, LazyExoticComponent } from "react";
import { lazy } from "react";

/**
 * Lazy load a component dynamically
 * Useful for route-based code splitting
 */
export function lazyImport<T extends Record<string, ComponentType<unknown>>>(
  importFunc: () => Promise<T>,
  name: keyof T
): LazyExoticComponent<ComponentType<unknown>> {
  return lazy(() =>
    importFunc().then((module) => ({
      default: module[name],
    }))
  );
}

/**
 * Performance monitoring utility
 * Tracks Core Web Vitals and other performance metrics
 */
export function initializePerformanceMonitoring() {
  // Measure Largest Contentful Paint (LCP)
  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
          renderTime?: number;
          loadTime?: number;
        };
        console.log("LCP:", lastEntry.renderTime || lastEntry.loadTime);
      });
      observer.observe({ entryTypes: ["largest-contentful-paint"] });
    } catch (e) {
      console.debug("LCP monitoring not available");
    }

    // Measure First Input Delay (FID) / Interaction to Next Paint (INP)
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const inpEntry = entry as PerformanceEntry & { processingDuration?: number };
          console.log("INP:", inpEntry.processingDuration);
        });
      });
      observer.observe({ entryTypes: ["first-input", "event"] });
    } catch (e) {
      console.debug("INP monitoring not available");
    }

    // Measure Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const clsEntry = entry as PerformanceEntry & {
            hadRecentInput?: boolean;
            value?: number;
          };
          if (!clsEntry.hadRecentInput) {
            clsValue += clsEntry.value ?? 0;
            console.log("CLS:", clsValue);
          }
        });
      });
      observer.observe({ entryTypes: ["layout-shift"] });
    } catch (e) {
      console.debug("CLS monitoring not available");
    }
  }

  // Monitor navigation timing
  if ("navigation" in window.performance) {
    window.addEventListener("load", () => {
      const perfData = window.performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (perfData) {
        console.log("Navigation Timing:", {
          dns: perfData.domainLookupEnd - perfData.domainLookupStart,
          tcp: perfData.connectEnd - perfData.connectStart,
          ttfb: perfData.responseStart - perfData.requestStart,
          download: perfData.responseEnd - perfData.responseStart,
          domInteractive: perfData.domInteractive - perfData.fetchStart,
          domComplete: perfData.domComplete - perfData.fetchStart,
        });
      }
    });
  }
}

/**
 * Prefetch resources for better performance
 */
export function prefetchResource(
  href: string,
  type: "prefetch" | "preload" | "dns-prefetch" = "prefetch"
) {
  const link = document.createElement("link");
  link.rel = type;
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Optimize image loading with intersection observer
 */
export function observeImages() {
  if ("IntersectionObserver" in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
          }
          imageObserver.unobserve(img);
        }
      });
    });

    document.querySelectorAll("img[data-src]").forEach((img) => {
      imageObserver.observe(img);
    });
  }
}
